import { FC } from "react";
import {
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";
import DefaultClientsForm, { ClientFormData } from "../Forms/DefaultClientForm";
import { Client } from "@/types";
import { createClient } from "@/api/clients";
import { useSnackbar, VariantType } from "notistack";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  extractCustomFieldsFromFormData,
  processCustomAttributesForCreation,
} from "../Forms/utils/customAttributeUtils";

interface CreateClientFlyoutProps {
  open: boolean;
  onSubmit: (client: Client) => void;
  handleClose: () => void;
}

const CreateClientFlyout: FC<CreateClientFlyoutProps> = ({
  open,
  onSubmit,
  handleClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  if (!open) return null;

  const handleSubmit = async (data: ClientFormData) => {
    try {
      const customAttributes = processCustomAttributesForCreation(data);
      const cleanedFormData = extractCustomFieldsFromFormData(data);
      const payload = {
        ...cleanedFormData,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      const createdClient = await createClient(payload);

      const newClient: Client = {
        ...cleanedFormData,
        ...createdClient,
        client_id: createdClient.client_id,
        isContact: 0,
      };

      onSubmit(newClient);

      enqueueSnackbar(`Client ${data?.name || ""} created`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created Client: ${data?.name}`,
        event: "client:create",
        object_type: "client",
      });

      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <Flyout open={open} onClose={handleClose}>
      <FlyoutHeader>
        <FlyoutTitle>Create Client</FlyoutTitle>
      </FlyoutHeader>
      <div style={{ padding: "20px", flex: 1, overflow: "auto" }}>
        <DefaultClientsForm
          onSubmit={handleSubmit}
          onCancel={handleClose}
          buttonProps={{ submitText: "Create Client" }}
          isFlyout={true}
        />
      </div>
    </Flyout>
  );
};

export default CreateClientFlyout;
